import { useState } from "react";
import { v4 as uuid } from "uuid";
import type {
  Variable,
  Operation,
  LogicalOperator,
  SubConditionData,
} from "./types";

export const useCondition = () => {
  const [selectedVariable, setSelectedVariable] = useState<Variable | null>(
    null,
  );
  const [selectedOperation, setSelectedOperation] = useState<Operation | null>(
    null,
  );
  const [comparativeValue, setComparativeValue] = useState<string | Variable>(
    "",
  );
  const [logicalOperator, setLogicalOperator] =
    useState<LogicalOperator>("and");
  const [additionalConditions, setAdditionalConditions] = useState<
    SubConditionData[]
  >([]);

  const handleVariableChange = (variable: Variable | null) => {
    setSelectedVariable(variable);
    setComparativeValue("");
    setSelectedOperation(null);
  };

  const handleOperationChange = (operation: Operation | null) => {
    setComparativeValue("");
    setSelectedOperation(operation);
  };

  const addCondition = () => {
    if (
      !selectedVariable ||
      !selectedOperation ||
      (selectedOperation.needsComparativeValue && comparativeValue === "")
    ) {
      return;
    }

    setAdditionalConditions((prev) => [
      ...prev,
      {
        id: uuid(),
        variable: null,
        operation: null,
        comparativeValue: "",
      },
    ]);
  };

  const updateCondition = (id: string, updates: Partial<SubConditionData>) => {
    setAdditionalConditions((prev) =>
      prev.map((condition) =>
        condition.id === id ? { ...condition, ...updates } : condition,
      ),
    );
  };

  const deleteCondition = (id: string) => {
    setAdditionalConditions((prev) =>
      prev.filter((condition) => condition.id !== id),
    );
  };

  const areAdditionalConditionsValid = additionalConditions.every(
    (condition) =>
      condition.variable &&
      condition.operation &&
      (!condition.operation.needsComparativeValue ||
        (condition.operation.needsComparativeValue &&
          condition.comparativeValue !== "")),
  );

  const isAddConditionDisabled =
    !selectedVariable ||
    !selectedOperation ||
    (selectedOperation.needsComparativeValue && comparativeValue === "") ||
    !areAdditionalConditionsValid;

  return {
    selectedVariable,
    selectedOperation,
    comparativeValue,
    logicalOperator,
    additionalConditions,
    handleVariableChange,
    handleOperationChange,
    setComparativeValue,
    setLogicalOperator,
    addCondition,
    updateCondition,
    deleteCondition,
    isAddConditionDisabled,
  };
};
